// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { SubInputs } from 'admin/common/styles/Styles';
import { FormFieldNumber } from 'common/components/Form/FormFieldNumber/FormFieldNumber';
import { SIZE } from 'common/constants';
import {
    Column,
    Columns,
    FormFieldCheckbox,
    Section,
    Translate,
} from '@plesk/ui-library';
import { FORM } from 'admin/plan/constants/tests';
import { Unit } from 'common/helpers/units';
import PlanLimit from 'common/components/plan/components/PlanLimit';
import {
    IPlanBackupSettings,
    IPlanLimit,
    LimitName,
} from 'common/api/resources/Plan';
import { StorageType } from 'common/api/resources/StorageType';
import { Tooltip } from 'common/components/index';
import { FIELDS } from 'common/components/plan/PlanForm';

export interface IAdditionalOffersSectionProps {
    is_backup_available: boolean;
    is_additional_ips_available: boolean;
    is_high_availability_cr_enabled: boolean;
    isIncrementalBackupsAvailable: boolean;
    storageType: StorageType;
    backupsNumber: IPlanLimit;
    backupSettings: IPlanBackupSettings;
    isBillingIntegrationEnabled: boolean;
    fields?: FIELDS[];
    handleChangeIsBackupAvailable: (enabled: boolean) => void;
    is_disaster_recovery_enabled: boolean;
}

const AdditionalOffersSection: React.FC<IAdditionalOffersSectionProps> = ({
    is_backup_available,
    is_additional_ips_available,
    is_high_availability_cr_enabled,
    isIncrementalBackupsAvailable,
    storageType,
    backupsNumber,
    backupSettings,
    isBillingIntegrationEnabled,
    fields,
    handleChangeIsBackupAvailable,
    is_disaster_recovery_enabled,
}) => {
    const additionalOffersFields = [
        FIELDS.IS_HIGH_AVAILABILITY_CR_ENABLED,
        FIELDS.HIGH_AVAILABILITY_CR_PRICE,
        FIELDS.IS_BACKUP_AVAILABLE,
        FIELDS.BACKUP_PRICE,
        FIELDS.BACKUP_LIMIT,
        FIELDS.INCREMENTAL_BACKUP,
        FIELDS.IS_ADDITIONAL_IP_AVAILABLE,
        FIELDS.ADDITIONAL_IP_TOKENS,
        FIELDS.ISO_IMAGE_TOKENS,
        FIELDS.IS_DISASTER_RECOVERY_AVAILABLE,
        FIELDS.DISASTER_RECOVERY_PRICE,
    ];

    if (!fields?.some(item => additionalOffersFields.includes(item))) {
        return null;
    }

    const isIncrementalBackupEnabled = is_backup_available && backupSettings.is_incremental_backup_enabled;
    const isIncrementalBackupCanBeEnabled = storageType === StorageType.THIN_LVM && backupsNumber.is_enabled && isIncrementalBackupsAvailable;

    return (
        <Section title={<Translate content="plan.actionDialog.additionalOffers" />}>
            {fields.includes(FIELDS.IS_HIGH_AVAILABILITY_CR_ENABLED) && (
                <FormFieldCheckbox
                    name="is_high_availability_cr_enabled"
                    label={<Translate content="plan.actionDialog.isHighAvailabilityCRAvailable" />}
                />
            )}
            {isBillingIntegrationEnabled && fields.includes(FIELDS.HIGH_AVAILABILITY_CR_PRICE) && (
                <SubInputs>
                    <FormFieldNumber
                        name="high_availability_cr_price"
                        label={<Translate content="plan.actionDialog.highAvailabilityCRPrice" />}
                        fullDescription={<Translate content="plan.actionDialog.highAvailabilityCRPriceDescription" />}
                        disabled={!is_high_availability_cr_enabled}
                        min={0}
                        max={100}
                    />
                </SubInputs>
            )}
            {fields.includes(FIELDS.IS_BACKUP_AVAILABLE) && (
                <FormFieldCheckbox
                    name="is_backup_available"
                    label={<Translate content="plan.actionDialog.isBackupAvailable" />}
                    data-cy={FORM.BACKUP_AVAILABILITY}
                    onChange={handleChangeIsBackupAvailable}
                />
            )}
            <SubInputs>
                {isBillingIntegrationEnabled && fields.includes(FIELDS.BACKUP_PRICE) && (
                    <FormFieldNumber
                        name="backup_price"
                        label={<Translate content="plan.actionDialog.backupPrice" />}
                        fullDescription={<Translate content="plan.actionDialog.backupPriceDescription" />}
                        data-cy={FORM.BACKUP_PRICE}
                        disabled={!is_backup_available}
                        min={0}
                        max={100}
                    />
                )}
                {fields.includes(FIELDS.BACKUP_LIMIT) && (
                    <PlanLimit
                        data-cy={FORM.BACKUP_LIMITS.NUMBER}
                        limit={backupsNumber}
                        limitName={LimitName.BackupsNumber}
                        limitOnly={isIncrementalBackupEnabled}
                        limitOnlyTooltip={<Translate content="plan.actionDialog.backupLimitOnly" />}
                        units={Unit}
                        label={<Translate content="plan.actionDialog.limits.backupsNumber.label" />}
                        fullDescription={<Translate content="plan.actionDialog.limits.backupsNumber.description" />}
                        disabled={!is_backup_available}
                    />
                )}
                {isIncrementalBackupsAvailable && fields.includes(FIELDS.INCREMENTAL_BACKUP) &&(
                    <>
                        <Tooltip
                            shown={!isIncrementalBackupCanBeEnabled}
                            title={<Translate content="plan.actionDialog.incrementalBackupCantBeEnabled" />}
                        >
                            <FormFieldCheckbox
                                name="backup_settings[is_incremental_backup_enabled]"
                                label={<Translate content="plan.actionDialog.isIncrementalBackupEnabled" />}
                                disabled={!is_backup_available || !isIncrementalBackupCanBeEnabled}
                            />
                        </Tooltip>
                        <FormFieldNumber
                            label={<Translate content="plan.actionDialog.incrementalCount" />}
                            name={'backup_settings[incremental_backups_limit]'}
                            min={1}
                            disabled={!isIncrementalBackupEnabled}
                        />
                    </>
                )}
                {fields?.includes(FIELDS.IS_DISASTER_RECOVERY_AVAILABLE) && (
                    <FormFieldCheckbox
                        name="is_disaster_recovery_enabled"
                        label={<Translate content="plan.actionDialog.isDisasterRecoveryEnabled" />}
                        disabled={!is_backup_available}
                    />
                )}
                {isBillingIntegrationEnabled && fields?.includes(FIELDS.DISASTER_RECOVERY_PRICE) && (
                    <FormFieldNumber
                        name="disaster_recovery_price"
                        label={<Translate content="plan.actionDialog.disasterRecoveryPrice" />}
                        fullDescription={<Translate content="plan.actionDialog.disasterRecoveryPriceDescription" />}
                        disabled={!is_disaster_recovery_enabled}
                        min={0}
                        max={100}
                    />
                )}
            </SubInputs>
            {fields.includes(FIELDS.IS_ADDITIONAL_IP_AVAILABLE) && (
                <FormFieldCheckbox
                    name="is_additional_ips_available"
                    label={<Translate content="plan.actionDialog.isAdditionalIpsAvailable"/>}
                    data-cy={FORM.ADDITIONAL_IP_AVAILABILITY}
                />
            )}
            {isBillingIntegrationEnabled && fields.includes(FIELDS.ADDITIONAL_IP_TOKENS) && (
                <SubInputs>
                    <Columns gap={SIZE.LG} vertical={false}>
                        <Column width={150}>
                            <FormFieldNumber
                                name="ip_tokens_per_hour"
                                label={<Translate content="plan.actionDialog.ipTokensPerHour" />}
                                fullDescription={<Translate content="plan.actionDialog.planTokenPerHourDescription" />}
                                vertical
                                min={0}
                                disabled={!is_additional_ips_available}
                            />
                        </Column>
                        <Column width={150}>
                            <FormFieldNumber
                                name="ip_tokens_per_month"
                                label={<Translate content="plan.actionDialog.ipTokensPerMonth" />}
                                fullDescription={<Translate content="plan.actionDialog.planTokenPerMonthDescription" />}
                                vertical
                                min={0}
                                disabled={!is_additional_ips_available}
                            />
                        </Column>
                    </Columns>
                </SubInputs>
            )}
            {isBillingIntegrationEnabled && fields.includes(FIELDS.ISO_IMAGE_TOKENS) && (
                <>
                    <Translate content="plan.actionDialog.isoImageTokens" />
                    <Columns gap={SIZE.LG} vertical={false}>
                        <Column width={190}>
                            <FormFieldNumber
                                name="iso_image_tokens_per_hour"
                                label={<Translate content="plan.actionDialog.isoImageTokensPerHour" />}
                                fullDescription={<Translate content="plan.actionDialog.planTokenPerHourDescription" />}
                                vertical
                                min={0}
                            />
                        </Column>
                        <Column width={190}>
                            <FormFieldNumber
                                name="iso_image_tokens_per_month"
                                label={<Translate content="plan.actionDialog.isoImageTokensPerMonth" />}
                                fullDescription={<Translate content="plan.actionDialog.planTokenPerMonthDescription" />}
                                vertical
                                min={0}
                            />
                        </Column>
                    </Columns>
                </>
            )}
        </Section>
    );
};

export default AdditionalOffersSection;
